import axios from 'axios'
import { Capacitor } from '@capacitor/core'
import { stashPostAuthRedirect } from './redirect'
import { clearSessionStorage } from './session'

// Web vs native API base.
//
// On web the frontend calls a *relative* `/api/v1` that Vercel rewrites to the
// backend (see vercel.json), or `http://localhost:5000/api/v1` in dev — no CORS
// because it's same-origin. Inside the Capacitor iOS/Android shell there is no
// Vercel proxy: the app is served from `capacitor://localhost`, so a relative
// path resolves to the bundle and 404s. The native build must therefore hit the
// backend's ABSOLUTE origin (and that origin allow-lists `capacitor://localhost`
// in its CORS config). `@capacitor/core` is isomorphic — on web
// `isNativePlatform()` is false and this collapses to the original behaviour.
const WEB_API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000/api/v1';
// Production backend lives on Railway (railway.json / nixpacks.toml; it also
// hosts the Postgres DB). The onrender.com host referenced by an older
// vercel.json rewrite is a stale Render leftover with demo data and no new
// deploys — do NOT point the native app there.
const NATIVE_API_URL = import.meta.env.VITE_NATIVE_API_URL || 'https://pawgroomer-production.up.railway.app/api/v1';
const API_URL = Capacitor?.isNativePlatform?.() ? NATIVE_API_URL : WEB_API_URL;

// Single source of truth for the resolved backend base, for the few call sites
// that issue raw fetch()/socket.io connections instead of going through the
// axios `api` instance below. Importing this (rather than re-reading
// import.meta.env.VITE_API_URL locally) means the web/native split is decided
// in exactly one place.
export const API_BASE = API_URL;

// Build-time secret that lets the native (Capacitor) shell skip web-only gates
// the WKWebView can't run — notably Cloudflare Turnstile, whose challenge can't
// validate the capacitor:// origin. CRITICAL: this is injected ONLY into the
// native build (VITE_NATIVE_BYPASS_TOKEN in the local native .env). Vite inlines
// `import.meta.env.*` at build time, so the web build (Vercel, no such env)
// compiles this to `undefined` — the secret never ships in the public web
// bundle and the header isn't sent on web. A static, guessable header would let
// anyone reading the web JS bypass Turnstile; a long random, native-only,
// rotatable secret does not. App Attest is the planned v1.1 hardening.
const NATIVE_BYPASS_TOKEN = import.meta.env.VITE_NATIVE_BYPASS_TOKEN;

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
    ...(Capacitor?.isNativePlatform?.() && NATIVE_BYPASS_TOKEN
      ? { 'X-Petzy-Native': NATIVE_BYPASS_TOKEN }
      : {})
  }
})

// Request interceptor — attach the Authorization header from localStorage.
// Paired with AuthContext, which is the only writer; there is no
// sessionStorage fallback anymore (persistent-session contract means
// localStorage is the single source of truth).
api.interceptors.request.use(
  (config) => {
    let token = null
    try { token = localStorage.getItem('token') } catch { /* noop */ }
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error) => Promise.reject(error)
)

// Response interceptor - handle 401 (unauthorized) + 402 (subscription required)
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Clear the WHOLE session, not just token+user. An impersonated session
      // whose token expires mid-use hits this path; leaving `impersonating` +
      // `adminToken` behind kept the red banner stuck on /login with an
      // orphaned admin token (Gemini review on #1606). Shared with
      // AuthContext.clearSession via utils/session so the two can't drift.
      clearSessionStorage()
      if (window.location.pathname !== '/login') {
        // Stash the page the user was on so LoginPage can bounce them
        // back after they re-auth. Uses sessionStorage (not local) for
        // the redirect handoff — it's intentionally short-lived: the
        // bounce should fire on the VERY NEXT login and not survive
        // across sessions.
        stashPostAuthRedirect(window.location.pathname + window.location.search)
        window.location.href = '/login'
      }
    }
    // 402 = hard-lock by requireActiveSubscription middleware. Trial expired
    // + beyond grace period, no card on file, or sub dead. Redirect to the
    // billing page (which is NOT guarded, so it always loads) so the user
    // can activate. Don't redirect if they're already there — else we loop.
    if (error.response?.status === 402 && error.response?.data?.needsBilling) {
      if (!window.location.pathname.startsWith('/salon/billing')) {
        window.location.href = '/salon/billing?reason=trial_expired'
      }
    }
    return Promise.reject(error)
  }
)

export default api
